import { fmtNum } from '../ui/ListFoot';
import type { ComponentChildren } from 'preact';
import { ShowHide } from '../ui/ShowHide';
import { EdgeScroll } from '../ui/EdgeScroll';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import { BookTile } from '../ui/books';
import { Shelf } from '../ui/controls';
import { arriveAt, pinnedBottom, scrollUnderPinned } from '../ui/arrive';
import { PillMenu, SettingMenu } from '../ui/PillMenu';
import { ListFoot } from '../ui/ListFoot';
import { Chip, ChipRow } from '../ui/bits';
import { BookCell, ListRow, ListTable, type Col } from '../ui/ListTable';
import { useRemembered } from '../lib/remember';
import type { FilterLists } from './filters';
import { LETTERS, azOrder, fileLetter, groupByGenre, sortList, subLine, type RefBook, type SortKey } from './reference';
import { usePerRow } from './usePerRow';

// Shelves › Reference (Phase 6.2 Part 4; Kristi's mockup, Oct 28, from boards 2 and 3): By genre
// (Jump to ▾, a folding group per genre with its own subgenre chips) or A–Z (genre chips, their
// subgenres, a letter row); covers (two rows, then Show N more / Show all) or a list sorted by its
// headers (the one ListTable, R6). The order and view switches sit in the pinned bar (ShelvesPage).
// Consistency pass (R3, Kristi Nov 2): chips only narrow (P7: the Jump to chips went; Jump to ▾
// does that job above the panes too), none on means all (P2: no "All" chip), and A–Z's genre
// chips narrow like subgenres, several at once.

const num = fmtNum;
const SUBS_SHOWN = 6;

/**
 * The book you opened from Reference, so ← Back brings you back to it (Kristi, Oct 29: on a phone
 * the list started over at the top). Kept for this page load only.
 */
let returnTo: string | null = null;
export const rememberReturn = (id: string) => {
  returnTo = id;
};

export type RefOrder = 'genre' | 'az';
export type RefView = 'covers' | 'list';

export function ReferenceTab({
  books,
  all,
  lists,
  order,
  view,
  wide,
  open,
}: {
  /** What's showing (after the Reference filters). */
  books: RefBook[];
  /** Every Reference book (the grouping rule's "most shared" genre). */
  all: RefBook[];
  lists: FilterLists;
  order: RefOrder;
  view: RefView;
  wide: boolean;
  open: (b: RefBook) => () => void;
}) {
  const groups = useMemo(() => groupByGenre(books, all, lists), [books, all, lists]);
  const genreName = useMemo(() => new Map(lists.genres.map((g) => [g.id, g.short ?? g.name])), [lists]);
  const subName = useMemo(() => new Map(lists.subgenres.map((s) => [s.id, s.name])), [lists]);
  const names = (b: RefBook, k: 'genre' | 'subgenres') => (k === 'genre' ? b.genres.map((g) => genreName.get(g)).filter(Boolean).join(' · ') : b.subgenres.map((s) => subName.get(s)).filter(Boolean).join(' · '));
  // Opens a folded pane, then scrolls just under what's pinned, looking again while the page settles (ui/arrive.ts).
  const jump = (id: string) => arriveAt(`[data-shelf="ref-${id}"]`, [`ref-${id}`]);
  const real = groups.filter((g) => g.id !== 'none').length;
  return (
    <div class="ref">
      {/* One description per tab, under the tabs (check-up: a second, older one sat here). */}
      <div class="ref-sum-row">
        <p class="ref-sum">
          {num(books.length)} {books.length === 1 ? 'book' : 'books'} · {order === 'genre' ? `${real} ${real === 1 ? 'genre' : 'genres'}, biggest first` : 'A–Z by title'}
        </p>
        {/* No Jump to ▾ here (Kristi, Nov 2: it repeated the one on the first pane's heading, just below). */}
      </div>
      {!books.length ? <div class="heads-up">No Reference books match these filters. Clear one, or Clear them all.</div> : null}
      {order === 'genre' ? (
        <>
          <div class="shelves-list">
            {groups.map((g, i) => (
              <GenreGroup key={g.id} group={g} first={i === 0} wide={wide} view={view} lists={lists} names={names} open={open} others={groups} jump={jump} />
            ))}
          </div>
        </>
      ) : (
        <AZ books={books} lists={lists} view={view} wide={wide} names={names} open={open} />
      )}
    </div>
  );
}

/** A pane's subgenre picks (Kristi, Oct 29): Show only or Hide, several at once; remembered on this device. */
export type SubPick = { mode: 'show' | 'hide'; on: string[] };
const NO_PICK: SubPick = { mode: 'show', on: [] };
export const useSubPick = (genreId: string) => useRemembered<SubPick>(`ref:subs:${genreId}`, NO_PICK);

/** The books a pane's picks leave: Show only any of them, or Hide every one of them. */
export function applyPick(books: RefBook[], pick: SubPick): RefBook[] {
  if (!pick.on.length) return books;
  return books.filter((b) => (pick.mode === 'show' ? pick.on.some((s) => b.subgenres.includes(s)) : !pick.on.some((s) => b.subgenres.includes(s))));
}

/**
 * After a pick that shortens what's showing: if you'd scrolled down inside it, go back to its top
 * (just under the pinned bars) so you don't land in the next pane (Kristi, Oct 29).
 */
/** If you're scrolled past the top of this element, go back to it, just under the pinned bars. */
export function backToTop(selector: string) {
  window.requestAnimationFrame(() => {
    const el = document.querySelector<HTMLElement>(selector);
    if (!el || el.getBoundingClientRect().top >= pinnedBottom()) return;
    scrollUnderPinned(el);
    // For a moment after, stay there while the page settles (covers still arriving above or below).
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => scrollUnderPinned(document.querySelector<HTMLElement>(selector)));
    ro.observe(document.body);
    window.setTimeout(() => ro.disconnect(), 1000);
  });
}

/** The pane heading's count in words (R7 N4, Kristi Nov 2): "194 books", narrowed "48 of 194 books · 2 subgenres" or "· hiding 1". */
function paneCount(shown: number, total: number, pick: SubPick): string {
  const books = (n: number) => `${num(n)} ${n === 1 ? 'book' : 'books'}`;
  if (!pick.on.length) return books(total);
  const k = pick.on.length;
  return `${num(shown)} of ${books(total)} · ${pick.mode === 'hide' ? `hiding ${k}` : `${k} ${k === 1 ? 'subgenre' : 'subgenres'}`}`;
}

/**
 * One genre: a folding shelf (only the first starts open on a phone) whose heading and subgenre
 * chips stay pinned while you scroll through it, then hand over to the next (Kristi, Oct 29),
 * with Jump to ▾ in the heading.
 */
function GenreGroup({
  group,
  first,
  wide,
  view,
  lists,
  names,
  open,
  others,
  jump,
}: {
  group: ReturnType<typeof groupByGenre>[number];
  first: boolean;
  wide: boolean;
  view: RefView;
  lists: FilterLists;
  names: (b: RefBook, k: 'genre' | 'subgenres') => string;
  open: (b: RefBook) => () => void;
  others: ReturnType<typeof groupByGenre>;
  jump: (id: string) => void;
}) {
  const [kept, keepPick] = useSubPick(group.id);
  const setPick = (p: SubPick) => {
    keepPick(p);
    backToTop(`[data-shelf="ref-${group.id}"]`);
  };
  // Only picks that still have books here count (a subgenre may have left the genre since).
  const pick = { ...kept, on: kept.on.filter((s) => group.subs.some((x) => x.id === s)) };
  const shown = applyPick(group.books, pick);
  // The pane's pinned heading height, so its list's column headers can pin just under it.
  useEffect(() => {
    const sec = document.querySelector<HTMLElement>(`[data-shelf="ref-${group.id}"]`);
    const pin = sec?.querySelector<HTMLElement>('.shelf-pin');
    if (!sec || !pin) return;
    const set = () => sec.style.setProperty('--pane-pin-h', `${pin.offsetHeight}px`);
    set();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(set) : null;
    ro?.observe(pin);
    return () => ro?.disconnect();
  });
  return (
    <Shelf
      id={`ref-${group.id}`}
      name={group.name}
      count={paneCount(shown.length, group.books.length, pick)}
      startOpen={wide || first}
      pinHead
      seeAll={others.length > 1 ? <JumpMenu here={group.id} groups={others} jump={jump} /> : undefined}
      chips={group.subs.length ? <SubChips subs={group.subs} pick={pick} set={setPick} label={`${group.name} subgenres`} wide={wide} /> : undefined}
    >
      <RefBooks key={`${pick.mode}:${pick.on.join(',')}`} books={shown} view={view} wide={wide} lists={lists} genreId={group.id === 'none' ? null : group.id} names={names} open={open} pinTop="var(--pane-pin-h, 0px)" />
    </Shelf>
  );
}

/**
 * Jump to ▾ (R4: a Secondary pill, "Cooking · 194"): above the panes, every genre; in a pane's
 * pinned heading, the other genres, without scrolling back to the top.
 */
function JumpMenu({ here, groups, jump }: { here: string | null; groups: ReturnType<typeof groupByGenre>; jump: (id: string) => void }) {
  return (
    <PillMenu
      label="Jump to"
      ariaLabel="Jump to a genre"
      class="ref-jump-menu"
      choices={groups.filter((g) => g.id !== here).map((g) => ({ value: g.id, label: `${g.name} · ${num(g.books.length)}` }))}
      onPick={jump}
    />
  );
}

/**
 * A pane's subgenres (Kristi, Oct 29): Show only / Hide (the Filters panel's switch), then the
 * subgenres as filter chips, several at once; none on means all (R3 P2). The first six, then
 * "+ N more" / Show fewer (picked ones always show). On a phone the chips stay on one line and
 * scroll, with the edge (R9 M5); the switch stays put at the left.
 */
function SubChips({ subs, pick, set, label, wide }: { subs: { id: string; name: string; count: number }[]; pick: SubPick; set: (p: SubPick) => void; label: string; wide: boolean }) {
  const [more, setMore] = useState(false);
  const first = more ? subs : subs.slice(0, SUBS_SHOWN);
  const list = [...first, ...subs.filter((s) => pick.on.includes(s.id) && !first.includes(s))];
  const toggle = (id: string) => set({ ...pick, on: pick.on.includes(id) ? pick.on.filter((x) => x !== id) : [...pick.on, id] });
  return (
    <div class="ref-subs">
      <ShowHide mode={pick.mode} label={label} onPick={(m) => set({ ...pick, mode: m })} />
      <ChipRow label={label} scroll={!wide}>
        {list.map((s) => {
          const on = pick.on.includes(s.id);
          return <Chip key={s.id} label={on && pick.mode === 'hide' ? <s>{s.name}</s> : s.name} count={s.count} on={on} onToggle={() => toggle(s.id)} />;
        })}
        {subs.length > SUBS_SHOWN ? (
          <button type="button" class="btn-link" onClick={() => setMore(!more)}>
            {more ? 'Show fewer' : `+ ${subs.length - SUBS_SHOWN} more`}
          </button>
        ) : null}
      </ChipRow>
    </div>
  );
}

/** The letter row: on a phone one line that scrolls sideways with the edge (R9 M2). */
function Letters({ wide, children }: { wide: boolean; children: ComponentChildren }) {
  return wide ? <>{children}</> : <EdgeScroll label="the letters">{children}</EdgeScroll>;
}

const SORTS: [string, string][] = [
  ['title:a', 'Title A–Z'],
  ['title:d', 'Title Z–A'],
  ['author:a', 'Author A–Z'],
  ['author:d', 'Author Z–A'],
  ['genre:a', 'Genre'],
  ['subgenres:a', 'Subgenres'],
];

/** The phone's Sort menu (Kristi liked it, Oct 29): in the list, or in A–Z's pinned controls. "Sort: Title A–Z ▾" (R4). */
function SortMenu({ sort, setSort }: { sort: { key: SortKey; desc: boolean }; setSort: (s: { key: SortKey; desc: boolean }) => void }) {
  return (
    <span class="ref-sort">
      <SettingMenu
        name="Sort"
        choices={SORTS}
        value={`${sort.key}:${sort.desc ? 'd' : 'a'}`}
        onChange={(v) => {
          const [key, d] = v.split(':') as [SortKey, string];
          setSort({ key, desc: d === 'd' });
        }}
      />
    </span>
  );
}

/**
 * A–Z: genre chips (several at once, none on = every genre; R3 P2), then the picked genres'
 * subgenres, the letter row (empty letters greyed), the books.
 */
function AZ({ books, lists, view, wide, names, open }: { books: RefBook[]; lists: FilterLists; view: RefView; wide: boolean; names: (b: RefBook, k: 'genre' | 'subgenres') => string; open: (b: RefBook) => () => void }) {
  // Kept as one id before the genre chips took several (Nov 2): read either.
  const [keptGenres, keepGenres] = useRemembered<string[] | string | null>('ref:az:genre', []);
  const picked = Array.isArray(keptGenres) ? keptGenres : keptGenres ? [keptGenres] : [];
  const toggleGenre = (g: string) => {
    keepGenres(picked.includes(g) ? picked.filter((x) => x !== g) : [...picked, g]);
    backToTop('.ref-az');
  };
  const setPick = (p: SubPick) => {
    keepPick(p);
    backToTop('.ref-az');
  };
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'title', desc: false });
  const [jumpTo, setJumpTo] = useState<{ letter: string; n: number } | null>(null);
  const genres = lists.genres
    .map((g) => ({ id: g.id, name: g.short ?? g.name, count: books.filter((b) => b.genres.includes(g.id)).length }))
    .filter((g) => g.count > 0 || picked.includes(g.id));
  // Only picks that are still genres here count.
  const genre = picked.filter((g) => genres.some((x) => x.id === g));
  const inGenre = genre.length ? books.filter((b) => genre.some((g) => b.genres.includes(g))) : books;
  // One genre shares its subgenre picks with its pane on By genre (as before); several keep their own.
  const [kept, keepPick] = useSubPick(genre.length ? [...genre].sort().join('+') : 'all');
  const subs = genre.length
    ? lists.subgenres
        .filter((s) => genre.includes(s.genreId))
        .map((s) => ({ id: s.id, name: s.name, count: inGenre.filter((b) => b.subgenres.includes(s.id)).length }))
        .filter((s) => s.count > 0)
    : [];
  const pick = genre.length ? { ...kept, on: kept.on.filter((s) => subs.some((x) => x.id === s)) } : NO_PICK;
  const left = applyPick(inGenre, pick);
  const sorted = view === 'list' ? sortList(left, sort.key, sort.desc, names) : azOrder(left);
  const lettered = view === 'covers' || sort.key === 'title' || sort.key === 'author';
  const key: SortKey = view === 'list' ? sort.key : 'title';
  const has = new Set(sorted.map((b) => fileLetter(b, key)));
  const subsLabel = genre.length === 1 ? `${genres.find((g) => g.id === genre[0])?.name} subgenres` : 'Subgenres';
  // The pinned controls' height, so a letter lands just below them.
  const controls = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = controls.current;
    if (!el) return;
    const set = () => document.documentElement.style.setProperty('--ref-az-h', `${el.offsetHeight}px`);
    set();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(set) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, []);
  return (
    <section class="ref-az" aria-label="A to Z">
      {/* Pinned under the Filters bar while you scroll (Kristi, Oct 29: a letter far down the alphabet left the controls a long way back). */}
      <div class="ref-az-controls" ref={controls}>
      <div class="ref-subs ref-genres">
        <span class="ref-label" aria-hidden="true">
          Genre
        </span>
        <ChipRow label="Genre" scroll={!wide}>
          {genres.map((g) => (
            <Chip key={g.id} label={g.name} count={g.count} on={genre.includes(g.id)} onToggle={() => toggleGenre(g.id)} />
          ))}
        </ChipRow>
      </div>
      {genre.length && subs.length ? <SubChips subs={subs} pick={pick} set={setPick} label={subsLabel} wide={wide} /> : null}
      {lettered || (view === 'list' && !wide) ? (
        <div class="ref-letters-row">
        {lettered ? (
          <Letters wide={wide}>
        <div class="ref-letters" role="group" aria-label="Jump to a letter">
          {LETTERS.map((l) =>
            has.has(l) ? (
              <button key={l} type="button" class="ref-letter" onClick={() => setJumpTo({ letter: l, n: (jumpTo?.n ?? 0) + 1 })}>
                {l}
              </button>
            ) : (
              <span key={l} class="ref-letter none" aria-hidden="true">
                {l}
              </span>
            ),
          )}
        </div>
          </Letters>
        ) : null}
        {view === 'list' && !wide ? <SortMenu sort={sort} setSort={(x) => (setSort(x), backToTop('.ref-az'))} /> : null}
        </div>
      ) : null}
      </div>
      <RefBooks
        key={`${genre.join('+')}:${pick.mode}:${pick.on.join(',')}`}
        books={sorted}
        view={view}
        wide={wide}
        lists={lists}
        genreId={null}
        names={names}
        open={open}
        letters={lettered ? key : undefined}
        sort={sort}
        setSort={setSort}
        jumpTo={jumpTo}
        sortInControls={!wide}
        pinTop="var(--ref-az-h, 0px)"
      />
    </section>
  );
}

/**
 * The books: covers (two rows, then Show N more / Show all, more as you scroll) or the list
 * (Title · Author · Genre · Subgenres, a header sorts, again reverses; a Sort menu on a phone).
 */
function RefBooks({
  books,
  view,
  wide,
  lists,
  genreId,
  names,
  open,
  letters,
  sort: given,
  setSort: setGiven,
  jumpTo,
  sortInControls,
  pinTop = '0px',
}: {
  books: RefBook[];
  view: RefView;
  wide: boolean;
  lists: FilterLists;
  genreId: string | null;
  names: (b: RefBook, k: 'genre' | 'subgenres') => string;
  open: (b: RefBook) => () => void;
  /** A–Z: a letter over the first book of each letter. */
  letters?: SortKey;
  sort?: { key: SortKey; desc: boolean };
  setSort?: (s: { key: SortKey; desc: boolean }) => void;
  jumpTo?: { letter: string; n: number } | null;
  /** A–Z on a phone: the Sort menu sits in the pinned controls instead. */
  sortInControls?: boolean;
  /** What's pinned above the list's header besides the bars: A–Z's controls or the pane's heading. */
  pinTop?: string;
}) {
  const { ref, perRow } = usePerRow(!wide);
  const [own, setOwn] = useState<{ key: SortKey; desc: boolean }>({ key: 'title', desc: false });
  const sort = given ?? own;
  const setSort = setGiven ?? setOwn;
  const list = view === 'list' && !given ? sortList(books, sort.key, sort.desc, names) : books;
  const step = view === 'list' ? (wide ? 24 : 12) : perRow * 2;
  const [pages, setPages] = useState<number | 'all'>(1);
  const [allUpTo, setAllUpTo] = useState(0);
  const count = pages === 'all' ? Math.min(list.length, Math.max(allUpTo, step * 4)) : Math.min(pages * step, list.length);
  const visible = list.slice(0, count);

  // Back from a book you opened here: show enough to include it, then scroll it into view.
  const back = useRef(returnTo && list.some((b) => b.id === returnTo) ? returnTo : null);
  useEffect(() => {
    const id = back.current;
    if (!id) return;
    const at = list.findIndex((b) => b.id === id);
    // Everything up to it stays showing however many covers fit a row (that's measured a moment later).
    if (pages !== 'all' || allUpTo < at + 1) {
      setPages('all');
      setAllUpTo(Math.max(allUpTo, at + 1 + step));
      return;
    }
    const el = box.current?.querySelector<HTMLElement>(`[data-book="${id}"]`);
    if (!el) return;
    back.current = null;
    returnTo = null;
    const go = () => {
      const above = pinnedBottom() + (box.current?.closest('.ref-az') ? (document.querySelector<HTMLElement>('.ref-az-controls')?.offsetHeight ?? 0) : 0);
      window.scrollTo({ top: Math.max(0, window.scrollY + el.getBoundingClientRect().top - above - 12) });
    };
    window.setTimeout(go, 60);
    window.setTimeout(go, 400);
  });

  // Show all: more as the end comes into view.
  const end = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (pages !== 'all' || count >= list.length || !end.current || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((e) => e.some((x) => x.isIntersecting) && setAllUpTo((n) => Math.max(n, count) + step * 4), { rootMargin: '600px' });
    io.observe(end.current);
    return () => io.disconnect();
  }, [pages, count, list.length]);

  // A letter: show the books up to it, then scroll there.
  const box = useRef<HTMLDivElement>(null);
  const pending = useRef<string | null>(null);
  useEffect(() => {
    if (!jumpTo || !letters) return;
    const at = list.findIndex((b) => fileLetter(b, letters) === jumpTo.letter);
    if (at < 0) return;
    if (at >= count) {
      if (pages === 'all') setAllUpTo(at + step);
      else setPages(Math.ceil((at + 1) / step) + 1);
    }
    pending.current = jumpTo.letter;
  }, [jumpTo?.n]);
  useLayoutEffect(() => {
    if (!pending.current) return;
    const el = box.current?.querySelector(`[data-letter="${pending.current}"]`);
    if (el) {
      pending.current = null;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' }); // lands just under the pinned controls
    }
  });

  let last = '';
  const letterAt = (b: RefBook) => {
    if (!letters) return '';
    const l = fileLetter(b, letters);
    const show = l !== last ? l : '';
    last = l;
    return show;
  };

  // A re-sort starts the list over at its top (Kristi, Oct 29: after jumping to M, staying put means nothing in a new order).
  const resort = (s: { key: SortKey; desc: boolean }) => {
    setSort(s);
    const host = box.current?.closest('[data-shelf], .ref-az');
    if (host) backToTop(host.matches('.ref-az') ? '.ref-az' : `[data-shelf="${host.getAttribute('data-shelf')}"]`);
  };
  // The one table (R6): its headers sort (⇅ on the others, the sorted one teal with ▲ or ▼); again reverses.
  const cols: Col<SortKey>[] = [
    { label: 'Title', width: 'minmax(0, 2.4fr)', sort: 'title', book: true },
    { label: 'Author', width: 'minmax(0, 1.3fr)', sort: 'author' },
    { label: 'Genre', width: 'minmax(0, 0.9fr)', sort: 'genre' },
    { label: 'Subgenres', width: 'minmax(0, 1.8fr)', sort: 'subgenres' },
  ];
  const onSort = (k: SortKey) => resort({ key: k, desc: sort.key === k ? !sort.desc : false });

  return (
    <div ref={box}>
      {view === 'covers' ? (
        <div ref={ref} class="tiles">
          {visible.map((b) => {
            const l = letterAt(b);
            return (
              <div key={b.id} class="tile-wrap" data-letter={l || undefined} data-book={b.id}>
                {letters ? <span class={`tile-month${l ? '' : ' blank'}`}>{l || ' '}</span> : null}
                <BookTile
                  book={{ id: b.id, title: b.title, author: b.author, coverUrl: b.cover, readType: b.readType, subgenre: subLine(b, lists, genreId) }}
                  onOpen={open(b)}
                />
              </div>
            );
          })}
          <span ref={end} class="tiles-end" aria-hidden="true" />
        </div>
      ) : (
        <>
          {!wide && !sortInControls ? <SortMenu sort={sort} setSort={resort} /> : null}
          <ListTable
            label="Reference books"
            cols={cols}
            sort={sort}
            onSort={onSort}
            narrow={!wide}
            class="ref-table"
            stickyTop={`calc(var(--top-bar-h, 0px) + var(--shelves-top-h, 0px) - 8px + ${pinTop})`}
          >
            {visible.map((b) => {
              const l = letterAt(b);
              const book = <BookCell title={b.title} cover={b.cover ?? null} onOpen={open(b)} author={wide ? undefined : [b.author, names(b, 'subgenres')].filter(Boolean).join(' · ')} />;
              return (
                <ListRow
                  key={b.id}
                  data-letter={l || undefined}
                  data-book={b.id}
                  cells={[book, b.author, names(b, 'genre'), names(b, 'subgenres')]}
                  phone={{ book }}
                />
              );
            })}
          </ListTable>
          <span ref={end} class="tiles-end" aria-hidden="true" />
        </>
      )}
      <ListFoot
        shown={pages === 'all' ? list.length : count}
        total={list.length}
        page={step}
        onMore={() => setPages(typeof pages === 'number' ? pages + 1 : pages)}
        onAll={() => setPages('all')}
        onFewer={() => setPages(1)}
      />
    </div>
  );
}
